// ===== FIELD PROJECTIONS =====
// Common field projections to reduce redundancy

// User information fields
export const USER_INFO_FIELDS = `
  _id
  userId
  name
  subId
`;

// Engagement metrics
export const ENGAGEMENT_FIELDS = `
  views
  downloads
  helpful
  notHelpful
`;

// Extended engagement metrics
export const ENGAGEMENT_EXTENDED_FIELDS = `
  views
  downloads
  helpful
  notHelpful
  averageTimeSpent
  shareCount
`;

// Podcast specific engagement
export const ENGAGEMENT_PODCAST_FIELDS = `
  views
  downloads
  helpful
  notHelpful
  averageTimeSpent
  listenCount
  shareCount
`;

// Pagination fields
export const PAGINATION_FIELDS = `
  total
  page
  limit
  pages
`;

// Thumbnail fields
export const THUMBNAIL_FIELDS = `
  url
  canEdit
  canDelete
`;

// Thumbnail with dimensions
export const THUMBNAIL_WITH_DIMENSIONS = `
  url
  dimensions {
    width
    height
  }
  canEdit
  canDelete
`;

// Access control fields
export const ACCESS_CONTROL_FIELDS = `
  internalEmployees {
    allSelected
    departments {
      name
      count
    }
    specificUsers {
      userId
      email
      name
      role
      subId
    }
  }
  userGroups {
    type
    count
  }
  participantCount
  participantLimit
`;

// Common content metadata
export const CONTENT_META_FIELDS = `
  subCategory
  tags
  createdAt
  updatedAt
  publishedAt
`;
// removed 'categoryIds' because of image, video, and document
// removed 'status' because of image, video, and document

// Video file fields
export const VIDEO_FILE_FIELDS = `
  _id
  title
  description
  url
  fileType
  fileSize
  duration
  order
  status
  uploadedAt
  thumbnail {
    ${THUMBNAIL_FIELDS}
  }
  playlists {
    playlistId
    position
  }
`;

// Image file fields
export const IMAGE_FILE_FIELDS = `
  title
  description
  url
  fileType
  fileSize
  dimensions {
    width
    height
  }
  order
  uploadedAt
  thumbnail {
    ${THUMBNAIL_WITH_DIMENSIONS}
  }
  status
  visibility
  allowDownload
  allowShare
  categoryIds
`;

// Document file fields
export const DOCUMENT_FILE_FIELDS = `
  title
  description
  url
  status
  fileSize
  fileType
  uploadedAt
  categoryIds
  tags
  visibility
  allowDownload
  allowShare
  pageCount
`;

// ===== CONTENT TYPE PROJECTIONS =====

// Article projection
export const ARTICLE_PROJECTION = `
  ... on Article {
    _id
    title
    summary
    articleType
	categoryIds
  thumbnail
  isFavoritedByMe
  allowShare
  allowDownload
  scheduledPublishDate
	status
    ${CONTENT_META_FIELDS}
    visibility
    author_id
    createdBy {
      ${USER_INFO_FIELDS}
    }
    updatedBy {
      ${USER_INFO_FIELDS}
    }
    publishedBy {
      ${USER_INFO_FIELDS}
    }
    engagement {
      ${ENGAGEMENT_FIELDS}
    }
  }
`;

// Video projection
export const VIDEO_PROJECTION = `
  ... on Video {
    _id
    companyId
    companyName
    isFavoritedByMe
    sourceType
    categoryIds
    videos {
      ${VIDEO_FILE_FIELDS}
      playbackProgress {
        totalPlays
        completions
        averageViewTime
        dropOffPoints {
          timePoint
          dropOffCount
        }
      }
    }
    totalDuration
    hasAudio
    videoQuality
    ${CONTENT_META_FIELDS}
    visibility
    allowDownload
    allowShare
    accessControl {
      ${ACCESS_CONTROL_FIELDS}
    }
    createdBy {
      ${USER_INFO_FIELDS}
    }
    updatedBy {
      ${USER_INFO_FIELDS}
    }
    publishedBy {
      ${USER_INFO_FIELDS}
    }
    engagement {
      ${ENGAGEMENT_FIELDS}
    }
    scheduledPublishDate
    deleted
  }
`;

// Podcast projection
export const PODCAST_PROJECTION = `
  ... on Podcast {
    _id
    companyId
    companyName
    isFavoritedByMe
    sourceType
    title
    description
    categoryIds
    status
    ${CONTENT_META_FIELDS}
    visibility
    coverImage {
      url
      altText
      canEdit
      canDelete
    }
    allowDownload
    allowShare
    accessControl {
      ${ACCESS_CONTROL_FIELDS}
    }
    seasons {
      seasonId
      title
      description
      coverImage
      episodeCount
      status
      scheduledPublishDate
      podcastSeasonCreatedAt: createdAt
      podcastSeasonUpdatedAt: updatedAt
      createdBy {
        ${USER_INFO_FIELDS}
      }
      updatedBy {
        ${USER_INFO_FIELDS}
      }
      publishedBy {
        ${USER_INFO_FIELDS}
      }
      publishedAt
    }
    episodes {
      episodeId
      seasonId
      title
      description
      audioUrl
      duration
      fileSize
      fileType
      episodeNumber
      releaseDate
      listens
      status
      scheduledPublishDate
      podcastEpisodeCreatedAt: createdAt
      podcastEpisodeUpdatedAt: updatedAt
      publishedAt
      createdBy {
        ${USER_INFO_FIELDS}
      }
      updatedBy {
        ${USER_INFO_FIELDS}
      }
      publishedBy {
        ${USER_INFO_FIELDS}
      }
    }
    analytics {
      totalListens
      averageListeningTime
      completionRate
      topEpisodes {
        episodeId
        title
        listens
      }
    }
    engagement {
      ${ENGAGEMENT_PODCAST_FIELDS}
    }
    createdBy {
      ${USER_INFO_FIELDS}
    }
    updatedBy {
      ${USER_INFO_FIELDS}
    }
    publishedBy {
      ${USER_INFO_FIELDS}
    }
    scheduledPublishDate
    deleted
  }
`;

// Image projection
export const IMAGE_PROJECTION = `
  ... on Image {
    _id
    companyId
    companyName
    sourceType
    isFavoritedByMe
    metricesUser {
      helpful
      notHelpful
    }
    files {
      ${IMAGE_FILE_FIELDS}
      viewerSettings {
        enableDownload
        enableShare
        enableCopy
        showMetadata
      }
      metadata {
        camera
        lens
        exposure
        aperture
        iso
        focalLength
        dateTaken
        location {
          latitude
          longitude
          placeName
        }
      }
      analytics {
        views
        downloads
        averageTimeSpent
        popularTags {
          tag
          count
        }
      }
      accessControl {
        ${ACCESS_CONTROL_FIELDS}
      }
    }
    ${CONTENT_META_FIELDS}
    createdBy {
      ${USER_INFO_FIELDS}
    }
    updatedBy {
      ${USER_INFO_FIELDS}
    }
    publishedBy {
      ${USER_INFO_FIELDS}
    }
    engagement {
      ${ENGAGEMENT_FIELDS}
    }
    scheduledPublishDate
    deleted
  }
`;

// Document projection
export const DOCUMENT_PROJECTION = `
  ... on Document {
    _id
    sourceType
    companyId
    isFavoritedByMe
    scheduledPublishDate
    files {
      ${DOCUMENT_FILE_FIELDS}
    }
    ${CONTENT_META_FIELDS}
    createdBy {
      ${USER_INFO_FIELDS}
    }
    updatedBy {
      ${USER_INFO_FIELDS}
    }
    publishedBy {
      ${USER_INFO_FIELDS}
    }
    engagement {
      ${ENGAGEMENT_FIELDS}
    }
  }
`;

// Playlist projection
export const PLAYLIST_PROJECTION = `
  ... on Playlist {
    _id
    companyId
    isFavoritedByMe
    name
    description
    thumbnailUrl
    items {
      contentId
      title
      contentType
      addedAt
      thumbnailUrl
      addedBy {
        ${USER_INFO_FIELDS}
      }
    }
    visibility
    enabled
    allowShare
    allowDownload
    views
    contentCount
    createdAt
    updatedAt
    accessControl {
      ${ACCESS_CONTROL_FIELDS}
    }
    createdBy {
      ${USER_INFO_FIELDS}
    }
    updatedBy {
      ${USER_INFO_FIELDS}
    }
  }
`;

// Category projection
export const CATEGORY_PROJECTION = `
  ... on Category {
    _id
    name
    description
    thumbnailUrl
    companyId
    companyName
    isEnabled
    visibility
    allowDownloads
    allowShare
    parentCategoryId
    isSubcategory
    level
    path
    subcategoryCount
    viewCount
    createdAt
    updatedAt
    accessControl {
      internalEmployees {
        allSelected
        departments {
          name
          count
        }
        specificUsers {
          userId
          email
          name
          role
        }
      }
      userGroups {
        type
        count
      }
      participantCount
      participantLimit
    }
    contentCount {
      total
      videos
      articles
      podcasts
      documents
      images
    }
    createdBy {
      ${USER_INFO_FIELDS}
    }
    updatedBy {
      ${USER_INFO_FIELDS}
    }
  }
`;

// ===== SEARCH PROJECTIONS =====
// Search projections (simplified for search results)

export const SEARCH_ARTICLE_PROJECTION = `
  ... on Article {
    _id
    title
    summary
    articleType
    categoryIds
    subCategory
    tags
    status
    visibility
    author_id
    createdAt
    updatedAt
    publishedAt
    createdBy { ${USER_INFO_FIELDS} }
    updatedBy { ${USER_INFO_FIELDS} }
    publishedBy { ${USER_INFO_FIELDS} }
    engagement { ${ENGAGEMENT_FIELDS} }
  }
`;

export const SEARCH_VIDEO_PROJECTION = `
  ... on Video {
    _id
    videos {
      _id
      title
      description
      url
      status
      fileSize
      fileType
      uploadedAt
      duration
      thumbnail { ${THUMBNAIL_FIELDS} }
      playlists { playlistId position }
    }
    categoryIds
    subCategory
    tags
    createdAt
    updatedAt
    createdBy { ${USER_INFO_FIELDS} }
    publishedBy { ${USER_INFO_FIELDS} }
    updatedBy { ${USER_INFO_FIELDS} }
    engagement { ${ENGAGEMENT_EXTENDED_FIELDS} }
  }
`;

export const SEARCH_PODCAST_PROJECTION = `
  ... on Podcast {
    _id
    title
    description
    categoryIds
    subCategory
    tags
    status
    visibility
    createdAt
    updatedAt
    seasons { episodeCount }
    publishedAt
    createdBy { ${USER_INFO_FIELDS} }
    updatedBy { ${USER_INFO_FIELDS} }
    publishedBy { ${USER_INFO_FIELDS} }
    engagement { ${ENGAGEMENT_EXTENDED_FIELDS} }
  }
`;

export const SEARCH_IMAGE_PROJECTION = `
  ... on Image {
    _id
    companyId
    sourceType
    files {
      title
      description
      url
      fileType
      fileSize
      categoryIds
    }
    subCategory
    tags
    engagement { ${ENGAGEMENT_EXTENDED_FIELDS} }
    imageCreatedAt: createdAt
    imageUpdatedAt: updatedAt
    createdBy { ${USER_INFO_FIELDS} }
    updatedBy { ${USER_INFO_FIELDS} }
  }
`;

export const SEARCH_DOCUMENT_PROJECTION = `
  ... on Document {
    _id
    companyId
    sourceType
    files {
      title
      description
      url
      fileType
      fileSize
      categoryIds
    }
    subCategory
    tags
    engagement { ${ENGAGEMENT_EXTENDED_FIELDS} }
    createdAt
    updatedAt
    createdBy { ${USER_INFO_FIELDS} }
    updatedBy { ${USER_INFO_FIELDS} }
  }
`;

export const SEARCH_PLAYLIST_PROJECTION = `
  ... on Playlist {
    _id
    name
    description
    visibility
    enabled
    items { contentId title addedAt }
    views
    contentCount
    createdBy { userId name }
    createdAt
  }
`;